import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { LegalDisclaimer } from "../components/LegalDisclaimer";
import { HelpLink } from "../help/links";
import { formatDate, formatMoney } from "../lib/format";
import {
  CLAIM_RESULT_LABELS,
  createClaim,
  reverseClaim,
  setClaimResult,
  type Claim,
  type ClaimResult,
  type ObligationLegal,
} from "../lib/legal";

/**
 * Претензионная работа по обязательству (E9, ТЗ 4.2.5): направление претензии, срок ответа,
 * индикация «возможно обращение в суд», результат рассмотрения, отмена ошибочной записи.
 *
 * Претензия — направленный документ: реквизиты НЕИЗМЕНЯЕМЫ. Опечатка исправляется отменой
 * (сторно-паттерн E8a) и новой записью; правится на месте только результат.
 *
 * Кнопки скрыты без права `legal:manage` — это лишь подсказка, настоящий запрет серверный.
 */

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const labelCls = "mb-1 block text-[13px] text-muted";

const RESULTS: ClaimResult[] = ["satisfied", "partially_satisfied", "rejected", "no_answer"];

export function ClaimsPanel({
  legal,
  canManage,
  onChanged,
}: {
  legal: ObligationLegal;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [resultFor, setResultFor] = useState<string | null>(null);
  const [reverseFor, setReverseFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const obligationId = legal.obligation.id;

  return (
    <div className="rounded-[var(--radius-l)] border border-border bg-surface p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[15px] font-bold text-ink">Претензии</h3>
        {canManage && !adding && (
          <button type="button" className={btnSecondary} onClick={() => setAdding(true)}>
            Зафиксировать претензию
          </button>
        )}
      </div>

      {legal.claimRequired && (
        <p className="mt-1 text-[13px] text-muted">
          По договору досудебный (претензионный) порядок обязателен.
        </p>
      )}

      {error !== null && (
        <p role="alert" className="mt-2 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}

      {adding && (
        <ClaimForm
          defaultAnswerDays={legal.defaultAnswerDays}
          suggestedAmount={suggestedAmount(legal)}
          calcOn={legal.calc.to}
          onCancel={() => setAdding(false)}
          onSubmit={async (input) => {
            const res = await createClaim(obligationId, input);
            if (!res.ok) {
              setError(res.message);
              return false;
            }
            setError(null);
            setAdding(false);
            onChanged();
            return true;
          }}
        />
      )}

      {legal.claims.length === 0 ? (
        <p className="mt-2 text-[13px] text-muted">
          {canManage
            ? "Претензии не направлялись."
            : "Претензии не направлялись. Претензионную работу ведёт юрист."}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {legal.claims.map((c) => (
            <li
              key={c.id}
              className="rounded-[var(--radius-m)] border border-border-soft p-2.5 text-[13px]"
            >
              <ClaimRow claim={c} />

              {/* Печатная форма расчёта к претензии (E9a): дату отсечки берёт сама форма из
                  претензии. Показана ВСЕМ, кто видит расчёт (печать — чтение, отдельного права
                  нет); у отменённой претензии тоже — лист печатается со штампом «ОТМЕНЕНА».
                  Записи об отмене своей печатной формы не имеют: документ — сама претензия. */}
              {c.reversesClaimId === null && (
                <div className="mt-2">
                  <Link
                    className="text-[13px] font-bold text-accent underline-offset-2 hover:underline"
                    to={`/print/calc/claim/${c.id}`}
                  >
                    Печатная форма расчёта
                  </Link>
                </div>
              )}

              {canManage && c.reversesClaimId === null && !c.reversed && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {c.result === null && (
                    <button
                      type="button"
                      className={btnSecondary}
                      onClick={() => setResultFor(resultFor === c.id ? null : c.id)}
                    >
                      Внести результат
                    </button>
                  )}
                  <button
                    type="button"
                    className={btnSecondary}
                    onClick={() => setReverseFor(reverseFor === c.id ? null : c.id)}
                  >
                    Отменить запись
                  </button>
                </div>
              )}

              {resultFor === c.id && (
                <ResultForm
                  onCancel={() => setResultFor(null)}
                  onSubmit={async (input) => {
                    const res = await setClaimResult(c.id, input);
                    if (!res.ok) {
                      setError(res.message);
                      return false;
                    }
                    setError(null);
                    setResultFor(null);
                    onChanged();
                    return true;
                  }}
                />
              )}

              {reverseFor === c.id && (
                <ReasonForm
                  label="Причина отмены претензии"
                  hint={
                    <>
                      Реквизиты претензии исправить нельзя: запись отменяется целиком, затем
                      заводится новая. <HelpLink topic="claimNotEditable" />
                    </>
                  }
                  submitLabel="Отменить претензию"
                  onCancel={() => setReverseFor(null)}
                  onSubmit={async (reason) => {
                    const res = await reverseClaim(c.id, reason);
                    if (!res.ok) {
                      setError(res.message);
                      return false;
                    }
                    setError(null);
                    setReverseFor(null);
                    onChanged();
                    return true;
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <LegalDisclaimer />
    </div>
  );
}

/** Одна запись ленты: обычная претензия либо отметка об отмене. */
function ClaimRow({ claim }: { claim: Claim }) {
  if (claim.reversesClaimId !== null) {
    return (
      <p className="text-muted">
        <span className="stamp">отмена</span> Запись от {formatDate(claim.sentDate)} отменена.
        Причина: {claim.reason}
      </p>
    );
  }

  return (
    <div className={claim.reversed ? "text-muted line-through" : "text-ink"}>
      <p className="font-bold">
        Претензия № {claim.outNumber} от {formatDate(claim.sentDate)}
        {claim.reversed && <span className="stamp ml-2 no-underline">отменена</span>}
      </p>
      <p className="mt-0.5 text-muted">
        Сумма требования: {formatMoney(claim.amount)}
        {claim.calcOn !== null && ` (расчёт на ${formatDate(claim.calcOn)})`} · автор:{" "}
        {claim.authorName}
      </p>

      {claim.term !== null && (
        <p className="mt-0.5">
          Срок ответа: {formatDate(claim.term.answerDue)} ({claim.answerDays} кал. дн.)
          {claim.term.shifted && (
            <span className="stamp ml-1" title="Перенос по ст. 193 ГК РФ">
              ⤷ перенос
            </span>
          )}
        </p>
      )}

      {claim.result !== null && (
        <p className="mt-0.5">
          Результат: {CLAIM_RESULT_LABELS[claim.result]}
          {claim.resultDate !== null && ` от ${formatDate(claim.resultDate)}`}
          {claim.resultNote !== null && ` — ${claim.resultNote}`}
        </p>
      )}

      {claim.term?.mayGoToCourt === true && (
        <p className="mt-1 rounded-[var(--radius-m)] bg-overdue-bg px-2 py-1 text-overdue-fg">
          Досудебный порядок соблюдён — возможно обращение в суд.
        </p>
      )}
    </div>
  );
}

/** Сумма требования по умолчанию — итог ОСНОВНОГО расчёта на выбранную дату. */
function suggestedAmount(legal: ObligationLegal): string | null {
  const primary = legal.calc.primary === "penalty" ? legal.calc.penalty : legal.calc.interest395;
  return primary === null || primary.total === "0.00" ? null : primary.total;
}

function ClaimForm({
  defaultAnswerDays,
  suggestedAmount: suggested,
  calcOn,
  onCancel,
  onSubmit,
}: {
  defaultAnswerDays: number;
  suggestedAmount: string | null;
  calcOn: string | null;
  onCancel: () => void;
  onSubmit: (input: {
    sentDate: string;
    outNumber: string;
    answerDays?: number;
    amount?: string;
    calcOn?: string;
  }) => Promise<boolean>;
}) {
  const [sentDate, setSentDate] = useState("");
  const [outNumber, setOutNumber] = useState("");
  const [answerDays, setAnswerDays] = useState(String(defaultAnswerDays));
  const [amount, setAmount] = useState(suggested ?? "");
  const [busy, setBusy] = useState(false);

  return (
    <form
      aria-label="Фиксация направленной претензии"
      className="mt-3 rounded-[var(--radius-m)] border border-border-soft p-3"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        void onSubmit({
          sentDate,
          outNumber: outNumber.trim(),
          answerDays: Number(answerDays),
          ...(amount.trim() === "" ? {} : { amount: amount.trim() }),
          ...(amount.trim() === "" || calcOn === null ? {} : { calcOn }),
        }).finally(() => setBusy(false));
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="claim-sent">
            Дата направления
          </label>
          <input
            id="claim-sent"
            type="date"
            required
            className={inputCls}
            value={sentDate}
            onChange={(e) => setSentDate(e.target.value)}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="claim-out">
            Исходящий №
          </label>
          <input
            id="claim-out"
            required
            className={inputCls}
            value={outNumber}
            onChange={(e) => setOutNumber(e.target.value)}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="claim-days">
            Срок ответа, календарных дней
          </label>
          <input
            id="claim-days"
            type="number"
            min={1}
            max={3650}
            required
            className={inputCls}
            value={answerDays}
            onChange={(e) => setAnswerDays(e.target.value)}
          />
          <p className="mt-1 text-[12px] text-muted">
            {defaultAnswerDays === 30
              ? "30 календарных дней — по ч. 5 ст. 4 АПК РФ (в договоре срок не задан)."
              : `${defaultAnswerDays} дн. — срок из договора.`}
          </p>
        </div>
        <div>
          <label className={labelCls} htmlFor="claim-amount">
            Сумма требования
          </label>
          <input
            id="claim-amount"
            className={inputCls}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <p className="mt-1 text-[12px] text-muted">
            {calcOn === null
              ? "Расчёт на выбранную дату недоступен."
              : `Подставлен итог расчёта на ${formatDate(calcOn)}.`}
          </p>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button type="submit" className={btnPrimary} disabled={busy}>
          {busy ? "Сохранение…" : "Зафиксировать"}
        </button>
        <button type="button" className={btnSecondary} onClick={onCancel} disabled={busy}>
          Отмена
        </button>
      </div>
      <LegalDisclaimer />
    </form>
  );
}

function ResultForm({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (input: {
    result: ClaimResult;
    resultDate?: string;
    resultNote?: string;
  }) => Promise<boolean>;
}) {
  const [result, setResult] = useState<ClaimResult>("rejected");
  const [resultDate, setResultDate] = useState("");
  const [resultNote, setResultNote] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <form
      aria-label="Результат рассмотрения претензии"
      className="mt-2 rounded-[var(--radius-m)] border border-border-soft p-3"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        void onSubmit({
          result,
          ...(resultDate === "" ? {} : { resultDate }),
          ...(resultNote.trim() === "" ? {} : { resultNote: resultNote.trim() }),
        }).finally(() => setBusy(false));
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="claim-result">
            Результат
          </label>
          <select
            id="claim-result"
            className={inputCls}
            value={result}
            onChange={(e) => setResult(e.target.value as ClaimResult)}
          >
            {RESULTS.map((r) => (
              <option key={r} value={r}>
                {CLAIM_RESULT_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="claim-result-date">
            Дата ответа
          </label>
          <input
            id="claim-result-date"
            type="date"
            className={inputCls}
            value={resultDate}
            onChange={(e) => setResultDate(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-3">
        <label className={labelCls} htmlFor="claim-result-note">
          Примечание
        </label>
        <input
          id="claim-result-note"
          className={inputCls}
          value={resultNote}
          onChange={(e) => setResultNote(e.target.value)}
        />
      </div>
      <div className="mt-3 flex gap-2">
        <button type="submit" className={btnPrimary} disabled={busy}>
          {busy ? "Сохранение…" : "Сохранить результат"}
        </button>
        <button type="button" className={btnSecondary} onClick={onCancel} disabled={busy}>
          Отмена
        </button>
      </div>
    </form>
  );
}

/** Форма «основание/причина» — общая для отмены претензии и правовых решений юриста. */
export function ReasonForm({
  label,
  hint,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  label: string;
  hint?: ReactNode;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (reason: string) => Promise<boolean>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const id = `reason-${label.replace(/\s+/g, "-")}`;

  return (
    <form
      aria-label={label}
      className="mt-2 rounded-[var(--radius-m)] border border-border-soft p-3"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        void onSubmit(reason.trim()).finally(() => setBusy(false));
      }}
    >
      <label className={labelCls} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        required
        className={inputCls}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {hint !== undefined && <p className="mt-1 text-[12px] text-muted">{hint}</p>}
      <div className="mt-3 flex gap-2">
        <button type="submit" className={btnPrimary} disabled={busy}>
          {busy ? "Сохранение…" : submitLabel}
        </button>
        <button type="button" className={btnSecondary} onClick={onCancel} disabled={busy}>
          Отмена
        </button>
      </div>
    </form>
  );
}
